import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { setAccount, clearAccount } from '@/features/msalAccount/msalAccountSlice';
import { EventType } from '@azure/msal-browser';

// Custom hook to synchronise MSAL account state with Redux state
export const useSyncMsalWithStore = () => {
	const { accounts, instance } = useMsal();
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	useEffect(() => {
		instance.addEventCallback((message) => {
			switch (message.eventType) {
			case EventType.ACCOUNT_ADDED:
			case EventType.LOGIN_SUCCESS:
			case EventType.ACQUIRE_TOKEN_SUCCESS: {
				// console.log('[MSAL] Account added', message);
				if (message?.payload?.account.username !== email) {
					const currentAccount = instance.getAccount(
						message?.payload?.account?.homeAccountId,
					);
					if (currentAccount) {
						// Pin MSAL's own active-account pointer to this genuine login/token
						// event so the accounts[]-polling effect below (which can't tell
						// cached accounts apart by order) stays in sync with it instead of
						// re-deriving from accounts[0].
						instance.setActiveAccount(currentAccount);
					}
					const payload = currentAccount
						? JSON.parse(JSON.stringify(currentAccount))
						: null;
					dispatch(setAccount(payload));
				}

				break;
			}
			case EventType.LOGOUT_SUCCESS:
			case EventType.ACCOUNT_REMOVED: {
				// console.log('[MSAL] Account removed', message);
				dispatch(clearAccount());
				break;
			}
			default:
				break;
			}
		});
	}, []);

	useEffect(() => {
		if (!accounts?.length) {
			return;
		}

		// MSAL does not guarantee stable ordering for getAllAccounts()/accounts
		// when more than one account is cached (common on PRE, which often has
		// a pentest account cached alongside a real one) — accounts[0] can
		// resolve to a different account on successive renders, which used to
		// ping-pong Redux's account (and every downstream user-data fetch)
		// between the cached accounts indefinitely. MSAL's own active-account
		// pointer is stable once set, so prefer it and only fall back to
		// accounts[0] to establish it the first time.
		const activeAccount = instance.getActiveAccount() ?? accounts[0];
		if (!instance.getActiveAccount()) {
			instance.setActiveAccount(activeAccount);
		}

		if (activeAccount?.username !== email) {
			const payload = activeAccount ? JSON.parse(JSON.stringify(activeAccount)) : null;
			// Dispatch to Redux
			dispatch(setAccount(payload));
		}
	}, [accounts, email, dispatch, instance]);
};
